import { ApolloCache, ApolloError } from '@apollo/client';

import { describeError, getRateLimitRejection } from '../../../common/rateLimit';

/**
 * What tagging and list changes share between the contact list (a selection)
 * and a contact's page (one person): limits, wording, error messages, and what
 * to forget from Apollo's cache after a change. Pure, so it is tested without
 * rendering.
 */

/** Tags one change adds or removes (the API's limit). */
export const MAX_TAGS_PER_CHANGE = 10;

/** Rows that can be ticked one by one: the API's `ids` limit. Larger selections go by the filter. */
export const MAX_TICKED = 1000;

export type BulkAction = 'addTag' | 'removeTag' | 'addList' | 'removeList';

export const isTagAction = (action: BulkAction) => action === 'addTag' || action === 'removeTag';
export const isAdding = (action: BulkAction) => action === 'addTag' || action === 'addList';

/** The menu entry and the dialog's title. */
export const ACTION_LABELS: Record<BulkAction, string> = {
  addTag: 'Add to tag',
  removeTag: 'Remove tag',
  addList: 'Add to list',
  removeList: 'Remove from list',
};

const n = (value: number) => value.toLocaleString('en-US');

export const people = (count: number) => `${n(count)} ${count === 1 ? 'contact' : 'contacts'}`;

/** A tag or list the admin picked, or one to make first ("Create tag 'x'"). */
export interface Target {
  /** Absent for one still to make. */
  id?: number;
  name: string;
  /** How many of the selection already have it (add) or have it to lose (remove), when counted. */
  have?: number;
}

/**
 * The line above the confirm button, so the effect is read before it happens:
 * "Add volunteer to 2,218 contacts. 1,014 already have it."
 */
export const describePlan = (action: BulkAction, targets: ReadonlyArray<Target>, count: number): string => {
  if (!targets.length) return '';
  const names = targets.map((t) => t.name).join(', ');
  const who = people(count);
  const counted = targets.length === 1 && targets[0].have !== undefined ? targets[0].have : undefined;

  switch (action) {
    case 'addTag':
      return `Add ${names} to ${who}.${counted ? ` ${n(counted)} already ${counted === 1 ? 'has' : 'have'} it.` : ''}`;
    case 'removeTag':
      return `Remove ${names} from ${who}.${counted !== undefined ? ` ${n(counted)} of them ${counted === 1 ? 'has' : 'have'} it.` : ''} Each removal is logged on the person's timeline.`;
    case 'addList':
      return `Add ${who} to ${names}.${counted ? ` ${n(counted)} ${counted === 1 ? 'is' : 'are'} on it already.` : ''}`;
    case 'removeList':
      return `Take ${who} off ${names}.${counted !== undefined ? ` ${n(counted)} of them ${counted === 1 ? 'is' : 'are'} on it.` : ''}`;
  }
};

/** The confirm button: what it does, and to how many. */
export const confirmLabel = (action: BulkAction, count: number): string => {
  switch (action) {
    case 'addTag':
      return `Tag ${people(count)}`;
    case 'removeTag':
      return `Untag ${people(count)}`;
    case 'addList':
      return `Add ${people(count)}`;
    case 'removeList':
      return `Take off ${people(count)}`;
  }
};

/** A finished change, from what the API answered. */
export interface ChangeOutcome {
  matched: number;
  changes: Array<{ name: string; changed: number }>;
}

const OUTCOME: Record<BulkAction, (name: string, changed: number, rest: number) => string> = {
  addTag: (name, changed, rest) => `${name}: added to ${people(changed)}${rest > 0 ? `; ${n(rest)} already had it` : ''}.`,
  removeTag: (name, changed, rest) => `${name}: removed from ${people(changed)}${rest > 0 ? `; ${n(rest)} did not have it` : ''}.`,
  addList: (name, changed, rest) =>
    `${name}: ${people(changed)} added${rest > 0 ? `; ${n(rest)} ${rest === 1 ? 'was' : 'were'} on it already` : ''}.`,
  removeList: (name, changed, rest) =>
    `${name}: ${people(changed)} taken off${rest > 0 ? `; ${n(rest)} ${rest === 1 ? 'was' : 'were'} not on it` : ''}.`,
};

/** "volunteer: added to 1,204 contacts; 1,014 already had it." One line per tag or list. */
export const describeOutcome = (action: BulkAction, outcome: ChangeOutcome): Array<string> =>
  outcome.changes.map(({ name, changed }) => OUTCOME[action](name, changed, outcome.matched - changed));

/* ------------------------------------------------------------------ *
 * Errors (CRM_CONTACT_QUERY_FRONTEND.md §17.6, §22)
 * ------------------------------------------------------------------ */

const firstError = (error: unknown) => (error instanceof ApolloError ? error.graphQLErrors?.[0] : undefined);

export const changeErrorCode = (error: unknown) => firstError(error)?.extensions?.code as string | undefined;

/**
 * The number the selection holds now, when the API refused a change because it
 * no longer held the number confirmed. Nothing was changed; confirm this one.
 */
export const selectionChangedTo = (error: unknown): number | undefined => {
  if (changeErrorCode(error) !== 'CRM_SELECTION_CHANGED') return undefined;
  const count = firstError(error)?.extensions?.count;
  return typeof count === 'number' ? count : undefined;
};

/** What to tell the admin when a change is refused. Nothing was changed in any of these. */
export const describeChangeError = (error: unknown): string => {
  if (getRateLimitRejection(error)) return describeError(error, 'Too many changes');

  const first = firstError(error);
  switch (changeErrorCode(error)) {
    case 'CRM_SELECTION_CHANGED': {
      const count = selectionChangedTo(error);
      return count === undefined
        ? 'The selection changed since it was counted. Nothing was changed.'
        : `The selection changed: it now holds ${people(count)}. Nothing was changed. Confirm the new number to go ahead.`;
    }
    case 'CRM_TAG_SYSTEM':
      return 'System tags are set by automation and cannot be added or removed by hand.';
    case 'CRM_TAG_NOT_FOUND':
      return 'That tag no longer exists. Close this and open it again to see the tags as they are now.';
    case 'CRM_TAG_NAME_TAKEN':
      return 'There is already a tag with that name, in some letter case. Pick it from the list instead.';
    case 'CRM_LIST_NOT_FOUND':
      return 'That list no longer exists. Close this and open it again to see the lists as they are now.';
    case 'CRM_LIST_WRONG_TYPE':
      return 'That is a saved filter: its people come from its filter, so nobody can be added to it or taken off it.';
    case 'CRM_CONTACT_MERGED':
      return 'A record in the selection was merged into another contact. Add the contact it was merged into instead.';
    case 'CRM_CONTACT_NOT_FOUND':
      return 'A contact in the selection no longer exists. Refresh the list and select again.';
    case 'CRM_QUERY_TIMEOUT':
      return 'The change took too long and was stopped. Nothing was changed. Narrow the filter and try again.';
    case 'CRM_INVALID_QUERY':
      return first?.message ?? 'The API refused this change.';
    case 'CRM_UNAVAILABLE':
      return 'The CRM database is not available on this server.';
    case 'FORBIDDEN':
      return 'Only admins can change tags and lists.';
    default:
      if (error instanceof ApolloError && error.networkError) {
        return 'Could not reach the API. The change may not have been made: check before trying again.';
      }
      return 'The change could not be made.';
  }
};

/** What the cache holds about who has which tag or list, outside one person's page. */
const LIST_FIELDS = ['crmContacts', 'crmContactCounts', 'crmContactTimeline', 'crmTags', 'crmSegments'];

/**
 * After tags or lists change, whatever the cache holds about who has which is
 * stale: pages of the list (a tag filter may no longer match), counts, each
 * tag's and list's total, and timelines. Forgetting them makes any screen still
 * showing them ask again, once.
 *
 * - `selection`: many people changed, so their own pages are forgotten too.
 * - `onePerson`: their page redraws from the change's answer, or asks again
 *   itself, so it is kept.
 */
export const forgetContactData = (cache: ApolloCache<unknown>, scope: 'selection' | 'onePerson') => {
  const fields = scope === 'selection' ? [...LIST_FIELDS, 'crmContact'] : LIST_FIELDS;
  fields.forEach((fieldName) => cache.evict({ id: 'ROOT_QUERY', fieldName }));
  cache.gc();
};
